import Promise from 'bluebird'
import auth0 from 'auth0-js'
import CommonError from '@/errors/common-error'
import store from '../store'

class Auth0Service {
  constructor () {
    /* eslint-disable no-console */
    console.log('Environment:', process.env)
    console.log('Auth0 Domain:', process.env.VUE_APP_AUTH0_DOMAIN)
    console.log('Auth0 Client ID:', process.env.VUE_APP_AUTH0_CLIENT_ID)
    /* eslint-enable no-console */
    this.renewalTimer = null
    // Hardcoded values with single quotes
    const domain = 'axisproto.auth0.com'
    const clientID = '2W2AwsIu6LgobDuA5bEXYzFf4vD1dtsZ'
    this.webAuth = new auth0.WebAuth({
      state: 'axisproto',
      domain: domain,
      clientID: clientID,
      connection: 'Username-Password-Authentication',
      responseType: 'code token id_token',
      redirectUri: `${window.location.protocol}//${window.location.host}/auth-callback`,
      scope: 'openid profile email'
    })
  }

  get token () { return localStorage.getItem('id_token') }
  set token (idToken) { localStorage.setItem('id_token', idToken) }
  get nonce () {
    const token = localStorage.getItem('id_token')
    try {
      const tokenObj = JSON.parse(atob(token.split('.')[1]))
      return tokenObj.nonce
    } catch (error) {
      console.error(error)
    }
  }

  get accessToken () { return localStorage.getItem('access_token') }
  set accessToken (accessToken) { localStorage.setItem('access_token', accessToken) }
  get expiresAt () { return localStorage.getItem('expires_at') }
  set expiresAt (expiresIn) {
    const expiresAt = JSON.stringify(expiresIn * 1000 + new Date().getTime())
    localStorage.setItem('expires_at', expiresAt)
  }

  isAuthenticated () { return new Date().getTime() < this.expiresAt }

  async login () {
    this.webAuth.authorize()
  }

  logout () {
    localStorage.removeItem('access_token')
    localStorage.removeItem('id_token')
    localStorage.removeItem('expires_at')
    localStorage.removeItem('user')
    clearTimeout(this.renewalTimer)
    this.webAuth.logout({
      returnTo: `${window.location.origin}/login`
    })
  }

  async resetPassword ({ email }) {
    return new Promise((resolve, reject) => {
      this.webAuth.changePassword({
        connection: 'Username-Password-Authentication',
        email
      }, function (error, result) {
        if (error) {
          if (error.policy) {
            return reject(new CommonError({
              title: error.message,
              message: error.policy
            }))
          }

          return reject(new CommonError({
            title: 'Error',
            message: error.description
          }))
        }

        resolve(result)
      })
    })
  }

  async signupAndAuthorize (args) {
    return new Promise((resolve, reject) => {
      this.webAuth.signupAndAuthorize({
        ...args,
        connection: 'Username-Password-Authentication'
      }, (error, result) => {
        if (error) {
          if (error.policy) {
            return reject(new CommonError({
              code: error.code,
              title: error.message,
              message: error.policy
            }))
          }

          return reject(new CommonError({
            code: error.code,
            title: 'Error',
            message: error.description
          }))
        }

        const idToken = result.idToken
        const jwtBody = idToken.split('.')[1].replace('-', '+').replace('_', '/')
        const jwtContent = atob(jwtBody)
        const decodedJwt = JSON.parse(jwtContent)
        const sub = decodedJwt.sub
        const userId = sub.split('|')[1]

        result.userId = userId

        resolve(result)
      })
    })
  }

  async parseHash () {
    return new Promise(resolve => {
      this.webAuth.parseHash((error, authResult) => {
        if (error) throw error
        resolve(authResult)
      })
    })
  }

  startRenewalTimer () {
    if (this.expiresAt) {
      const timeUntilRefresh = this.expiresAt - new Date().getTime()
      if (timeUntilRefresh <= 0) { return this.renewToken() }
      this.renewalTimer = setTimeout(() => this.renewToken(), timeUntilRefresh)
    }
  }

  async renewToken () {
    try {
      const authResult = await Promise.fromCallback(clb => {
        this.webAuth.checkSession({}, clb)
      })
      if (authResult && authResult.accessToken && authResult.idToken) {
        store.dispatch('auth/saveAuthPayload', authResult)
        this.startRenewalTimer()
      }
    } catch (error) {
      console.error(error)
      this.logout()
    }
  }
}

export default new Auth0Service()
